import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import buildSerializer from "lib/buildSerializer"
import theme from "styles/theme"
import BlockHeader from "components/BlockHeader"
import Chart from "components/charts/Chart"
import Container from "components/Container"
import Footnotes from "components/Footnotes"
import InlineImage from "components/InlineImage"
import ComplexTableBlock from "components/blocks/ComplexTableBlock"
import QuoteBlock from "components/blocks/QuoteBlock"
import StatCard from "components/cards/StatCard"
import TextBlock from "components/TextBlock"
import SidebarLinks from "components/SidebarLinks"
import { TableEntry } from "components/blocks/TableBlock"
import BlockContent from "components/BlockContent"
import ScrollIndicator from "components/ScrollIndicator"
import ArticleStoryCard from "components/ArticleStoryCard"

const expand = styles =>
  theme.expandQueries(styles, ["root", theme.tabletSmall, theme.mobile])

const ArticleBlock = ({ sections, scrollIndicator }) => {
  return (
    <div className="articleBlock">
      {sections.map(({ _key, title, overview, content }, sectionIndex) => {
        return (
          <div key={sectionIndex} id={`section-${_key}`}>
            {sectionIndex !== 0 && (
              <SectionHeader title={title} sectionNo={sectionIndex + 1} />
            )}

            <Container
              max={943}
              wrapperStyles={{
                "--inlinePadding": "64px",
                padding: "0 var(--inlinePadding)",
                [theme.mobile]: {
                  "--inlinePadding": "30px",
                },
                [theme.smallMobilePortrait]: {
                  "--inlinePadding": "20px",
                },
              }}
              css={expand({ padding: ["85px 0", "60px 10px", "35px 10px"] })}
            >
              <SectionOverview {...overview} />

              {chunkContent(content).map(
                ({ primary, sidebar, block }, index) => (
                  <div
                    key={index}
                    css={
                      (!block || content.length > 1) &&
                      expand({
                        display: "grid",
                        gridTemplateColumns: ["1fr 256px", "1fr 179px", "1fr"],
                        alignItems: "start",
                        gap: [38, 10],
                      })
                    }
                  >
                    {block ? (
                      <ContentChunk
                        css={expand({
                          gridColumn: "1 / -1",
                          marginTop: (() => {
                            if (block.style === "h4") {
                              return '1em'
                            }
                            return index > 0 ? [65, 55, 35] : 17
                          })(),
                          h3: block._type === "block" && {
                            ...theme.t4,
                            margin: 0,
                            sup: {
                              color: theme.gray,
                              fontSize: 11,
                              fontWeight: 700,
                              position: "relative",
                              top: ["-1em", "-0.6em"],
                              left: "0.2em",
                            },
                          },
                          h4: block._type === "block" && {
                            ...theme.t4sm,
                            margin: 0,
                            color: theme.midBlue,
                            sup: {
                              color: theme.gray,
                              fontSize: 11,
                              fontWeight: 700,
                              position: "relative",
                              top: ["-1em", "-0.6em"],
                              left: "0.2em",
                            },
                          },
                          "& :last-child": { marginBottom: 0 }
                        })}
                      >
                        {[block]}
                      </ContentChunk>
                    ) : (
                      <>
                        <ContentChunk
                          css={{ "& :last-child": { marginBottom: 0 } }}
                        >
                          {primary}
                        </ContentChunk>
                        {sidebar.length > 0 && (
                          <SidebarChunk css={{ paddingTop: "1em" }}>
                            {sidebar}
                          </SidebarChunk>
                        )}
                      </>
                    )}
                  </div>
                )
              )}
            </Container>
          </div>
        )
      })}

      {scrollIndicator && (
        <div css={{ background: theme.nearWhite }}>
          <ScrollIndicator
            css={expand({
              transform: [
                "translateY(-35px)",
                "translateY(-25px)",
                "translateY(-20px)",
              ],
              paddingTop: [5, 20, 30],
            })}
          >
            {scrollIndicator}
          </ScrollIndicator>
        </div>
      )}
    </div>
  )
}

export default ArticleBlock

ArticleBlock.propTypes = {
  sections: PropTypes.array.isRequired,
  scrollIndicator: PropTypes.string,
}

const SidebarChunk = props => (
  <BlockContent
    serializers={{
      types: {
        statCard: buildSerializer(StatCard, {
          condensed: true,
          css: {
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: 40,
            [theme.tabletSmall]: {
              marginBottom: 30,
            },
          },
        }),
        sidebarImage: buildSerializer(InlineImage, {
          imageProps: {
            width: 512,
          },
          sizes: "(min-width: 960px) 256px, (min-width: 768px) 179px, 80vw",
          css: expand({
            borderBottom: 0,
            color: theme.gray,
            margin: "0 0 40px",
            img: {
              borderBottom: `6px solid ${theme.midBlue}`,
            },
            [theme.tabletSmall]: {
              marginBottom: 30,
            },
            [theme.mobile]: {
              margin: "21px auto 30px",
              paddingLeft: 30,
              paddingRight: 30,
              maxWidth: 500,
            },
            [theme.smallMobilePortrait]: {
              padding: 0,
            },
          }),
        }),
        sidebarLinks: buildSerializer(SidebarLinks),
      },
    }}
    {...props}
  />
)

const ContentChunk = props => {
  return <TextBlock
    serializers={{
      types: {
        blockquote: buildSerializer(QuoteBlock),
        columnChart: buildSerializer(Chart, {
          propMap: { _type: "type" },
          css: expand({
            marginBottom: [65, 55, 45],
            [theme.tabletSmall]: {
              maxWidth: "calc(100vw - 20px)",
              // marginLeft: "calc(-1 * var(--inlinePadding))",
              // marginRight: "calc(-1 * var(--inlinePadding))",
            },
          }),
        }),
        pieChart: buildSerializer(Chart, {
          propMap: { _type: "type" },
          css: expand({
            marginTop: [65, 55],
            marginBottom: [65, 55],
            [theme.mobile]: {
              maxWidth: "calc(100vw - 20px)",
              marginTop: "45px",
              marginBottom: "45px",
              // margin: "45px calc(-1 * var(--inlinePadding))",
            },
          }),
        }),
        complexTableBlock: buildSerializer(ComplexTableBlock),
        imageChart: ImageChartNode,
        sectionFootnotes: buildSerializer(Footnotes, {
          propMap: {
            footnotes: "children",
          },
        }),
        tableEntry: buildSerializer(TableEntry, {
          css: {
            marginTop: "0 !important",
            marginBottom: 65,
            "--inlinePadding": "74px",
            [theme.tabletSmall]: {
              marginBottom: 55,
            },
            [theme.mobile]: {
              "--inlinePadding": "40px",
              marginBottom: 45,
            },
            [theme.smallMobilePortrait]: {
              "--inlinePadding": "30px",
            },
          },
        }),
        inlineImage: buildSerializer(InlineImage, {
          css: {
            "figcaption > p": {
              marginBottom: "1.176em !important",
            },
            img: {
              width: "100%",
            },
          },
        }),
        blockImage: buildSerializer(InlineImage, {
          imageProps: {
            width: 943,
          },
          css: {
            ":first-child": {
              marginTop: 0,
            },
            "figcaption > p": {
              marginBottom: "1.176em !important",
            },
            img: {
              width: "100%",
            },
          },
        }),
        storyCard: buildSerializer(ArticleStoryCard),
      },
    }}
    {...props}
  />
}

// Configuration for the block content chunker
const BLOCK_TYPES = ["columnChart", "tableEntry", "blockImage"]
const ASIDE_TYPES = ["statCard", "sidebarImage", "sidebarLinks"]

const newBuffer = () => ({
  primary: [],
  sidebar: [],
})

const chunkContent = entries => {
  const chunks = []
  let buffer = newBuffer()

  entries.forEach(entry => {
    if (
      BLOCK_TYPES.includes(entry._type) ||
      (entry._type === "block" && entry.style === "h3" || entry.style === "h4")
    ) {
      // Flush the buffer to chunks
      if (buffer.primary.length) {
        chunks.push(buffer)
        buffer = newBuffer()
      }
      // Add block content to chunks
      chunks.push({ block: entry })
    } else {
      // Add the content to the buffer
      if (ASIDE_TYPES.includes(entry._type)) {
        buffer.sidebar.push(entry)
      } else {
        buffer.primary.push(entry)
      }
    }
  })

  // Flush remaining content from the buffer
  if (buffer.primary.length) {
    chunks.push(buffer)
  }

  return chunks
}

const SectionHeader = ({ title, sectionNo }) => (
  <div
    css={expand({
      padding: ["90px 10px 75px", "65px 10px"],
      background: theme.white,
      borderTop: `8px solid ${theme.midBlue}`,
      borderTopWidth: [8, 6, 5],
    })}
  >
    <BlockHeader label={`Section ${sectionNo}`} title={title} />
  </div>
)
SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  sectionNo: PropTypes.number.isRequired,
}

const SectionOverview = ({ label, text }) => {
  if (!label && !text) {
    return null
  }
  return (
    <div
      css={expand({
        borderLeft: `4px solid ${theme.midBlue}`,
        borderLeftWidth: [4, 4, 2],
        paddingLeft: [24, 20, 10],
        marginBottom: [55 - 17, 45 - 15, 45 - 14],
      })}
    >
      {label.trim() &&
        <div css={{ ...theme.t5lg, color: theme.midBlue, marginBottom: 18 }}>
          {label}
        </div>
      }
      {text.trim() &&
        <div css={{ ...theme.blockquoteText }}>{text}</div>
      }
    </div>
  )
}
SectionOverview.propTypes = {
  label: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
}

const ImageChartNode = ({ node: { _type, ...props } }) => (
  <Chart
    type={_type}
    css={expand({
      marginTop: [65, 55],
      marginBottom: [65, 55],
      [theme.mobile]: {
        maxWidth: "calc(100vw - 20px)",
        marginTop: "45px",
        marginBottom: "45px",
        // margin: "45px calc(-1 * var(--inlinePadding))",
      },
    })}
    {...props}
  />
)

ImageChartNode.propTypes = {
  node: PropTypes.shape({
    _type: PropTypes.string.isRequired,
    layout: PropTypes.oneOf(["full", "half"]),
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.array.isRequired,
    }),
    footnotes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
}

export const query = graphql`
  fragment ArticleBlock on SanityArticleBlock {
    sections {
      _key
      title
      overview {
        label
        text
      }
      content: _rawContent(resolveReferences: { maxDepth: 10 })
    }
    scrollIndicator
  }
`
